import { Environment } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { createElement, useEffect, useRef } from "react";
import type { CubeTexture } from "three";
import { updateReflectionMap } from "../editor3d/ReflectionMap";
import type { SceneDefinition } from "./Manufacturer";
import { StandardEnvironment } from "./StandardEnvironment";

interface SceneRendererProps {
  scene?: SceneDefinition;
  resolution?: number;
}

/**
 * Renders a manufacturer-provided scene as backdrop for products
 * Falls back to StandardEnvironment if no scene is provided
 */
export const SceneRenderer = ({
  scene,
  resolution = 512,
}: SceneRendererProps) => {
  const { scene: threeScene } = useThree();
  const envMapRef = useRef<CubeTexture | null>(null);

  // Clean up when unmounted
  useEffect(() => {
    return () => {
      if (envMapRef.current) {
        threeScene.environment = null;
      }
    };
  }, [threeScene]);

  // If no scene is provided, use the StandardEnvironment
  if (!scene) {
    return <StandardEnvironment resolution={resolution} />;
  }

  // If the scene has a custom component, use it
  if (scene.component) {
    return createElement(scene.component);
  }

  // Handle environment map creation
  const handleEnvironmentCreated = (envMap: CubeTexture) => {
    // Store for later reference
    envMapRef.current = envMap;

    // Set global scene environment (affects MeshStandardMaterial)
    threeScene.environment = envMap;

    // Update reflection map for compatibility with older materials
    updateReflectionMap(envMap);
  };

  // If the scene has an environment map, use it
  if (scene.environmentMap) {
    return (
      <Environment
        files={scene.environmentMap}
        resolution={resolution}
        scene={threeScene}
        {...{ onCreated: handleEnvironmentCreated }}
      />
    );
  }

  // Default fallback to StandardEnvironment
  return <StandardEnvironment resolution={resolution} />;
};
